import { motion } from 'framer-motion';
import { ChevronRight, ExternalLink, FileText, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TODO_QUALITY_GRADIENT } from './ComplianceHubChrome';

export function AccessGuard({ message }: { message: string }) {
  return (
    <div className="p-6">
      <div className="min-h-[200px] rounded-3xl border border-dashed border-slate-200 bg-white/60 p-10 text-center flex items-center justify-center">
        <div>
          <p className="text-2xl font-semibold text-slate-700">ไม่สามารถเข้าถึงข้อมูลได้</p>
          <p className="mt-2 text-slate-500">{message}</p>
        </div>
      </div>
    </div>
  );
}

function homeNavCardClassName(selected = false): string {
  return `group flex min-h-[72px] w-full items-center gap-3 rounded-2xl border border-white/70 bg-white/75 p-3 text-left shadow-md backdrop-blur-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 sm:gap-3.5 sm:p-3.5 ${
    selected ? 'ring-2 ring-teal-500' : 'hover:shadow-lg'
  }`;
}

type HomeNavCardBase = {
  label: string;
  icon: LucideIcon;
  selected?: boolean;
  onClick?: () => void;
  trailing?: 'chevron' | 'external';
};

type HomeNavCardProps = HomeNavCardBase &
  (
    | { href: string; to?: never }
    | { to: string; href?: never }
    | { to?: undefined; href?: undefined }
  );

/** Mega-menu-style button box used on the Home hub. */
export function HomeNavCard({
  label,
  icon: Icon,
  selected = false,
  to,
  href,
  onClick,
  trailing,
}: HomeNavCardProps) {
  if (typeof href === 'string' && typeof to === 'string' && import.meta.env.DEV) {
    console.warn('HomeNavCard received both href and to; preferring href');
  }
  const resolvedTrailing = trailing ?? (href ? 'external' : 'chevron');
  const body = (
    <>
      <span
        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          selected ? 'bg-teal-100 text-teal-800' : 'bg-emerald-50 text-teal-700'
        }`}
        aria-hidden
      >
        <Icon className="h-5 w-5" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1 break-words text-left text-sm font-semibold leading-snug text-teal-800 sm:text-[15px]">
        {label}
      </span>
      <span
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          selected ? 'bg-teal-100 text-teal-800' : 'bg-teal-50 text-teal-700'
        }`}
        aria-hidden
      >
        {resolvedTrailing === 'external' ? (
          <ExternalLink className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={homeNavCardClassName(selected)}
      >
        {body}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={homeNavCardClassName(selected)}>
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={homeNavCardClassName(selected)}
      aria-pressed={selected}
      onClick={onClick}
    >
      {body}
    </button>
  );
}

export function QualityLinkCard({ label, url, tier }: { label: string; url: string; tier?: 'todo-quality' }) {
  const isTodoQuality = tier === 'todo-quality';
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ y: -2, boxShadow: '0 12px 28px rgba(15, 118, 110, 0.16)' }}
      className={`group flex min-h-[72px] w-full items-center gap-3 rounded-2xl border border-white/70 p-3 shadow-md backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-teal-500 sm:gap-3.5 sm:p-3.5 ${
        isTodoQuality ? '' : 'bg-white/75'
      }`}
      style={{
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        ...(isTodoQuality ? { background: TODO_QUALITY_GRADIENT } : {}),
      }}
      aria-label={`Open ${label}`}
    >
      <span
        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          isTodoQuality ? 'bg-white/55 text-teal-800' : 'bg-emerald-50 text-teal-700'
        }`}
        aria-hidden
      >
        <FileText className="h-5 w-5" strokeWidth={2} />
      </span>
      <span
        className={`min-w-0 flex-1 break-words text-left text-sm font-semibold leading-snug sm:text-[15px] ${
          isTodoQuality ? 'text-slate-900' : 'text-teal-800'
        }`}
      >
        {label}
      </span>
      <span
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          isTodoQuality ? 'bg-white/45 text-teal-800' : 'bg-teal-50 text-teal-700'
        }`}
        aria-hidden
      >
        <ExternalLink className="h-4 w-4" />
      </span>
    </motion.a>
  );
}
